import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { APP_VERSION } from "../constants/appVersion";

const utahData = "/img/UTDT_Logo.png";
const pdfIcon = "/img/pdf1.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

export default function AeroTable25({ data }) {
  const pdfRef = useRef();
  if (!data?.length) return <p>No Data Available</p>;

  // Extract years dynamically
  const years = Object.keys(data[0]).filter((key) => key.match(/\d{4}/));

  // =========================
  // Compute Totals & Averages
  // =========================
  const processedData = data.map((row) => {
    const values = years.map((year) => parseInt(row[year]) || 0);
    const total = values.reduce((a, b) => a + b, 0);
    const average = total / years.length;

    return {
      race: row.race_label,
      values,
      total,
      average,
    };
  });

  const grandTotal = processedData.reduce((sum, row) => sum + row.total, 0);

  const topAverageRace = processedData.reduce(
    (max, row) => (row.average > max.average ? row : max),
    processedData[0],
  );

  const overallAverage = grandTotal / (years.length * data.length);

  // =========================
  // Stable Professional Colors
  // =========================
  const palette = [
    "#0ea5e9",
    "#ec4899",
    "#10b981",
    "#f59e0b",
    "#6366f1",
    "#ef4444",
  ];

  // =========================
  // Chart Data
  // =========================
  const chartData = {
    labels: years,
    datasets: processedData.map((row, index) => ({
      label: row.race,
      data: row.values,
      backgroundColor: palette[index % palette.length],
      borderRadius: 6,
    })),
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📊 Employment by Race & Year",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Year" },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Employment Count",
        },
      },
    },
  };

  // =========================
  // PDF Export
  // =========================
const downloadPDF = () => {
  const input = pdfRef.current;
  html2canvas(input, {
    scale: 2,
    useCORS: true,
    backgroundColor: "#ffffff",
    scrollY: -window.scrollY,
  }).then((canvas) => {
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth - 10;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;
    let pageNumber = 1;

    const addHeader = () => {
      const logoWidth = 20; // ideal header width
      const logoHeight = 20; // adjust ratio if needed

      pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
      pdf.setFontSize(26);
      pdf.text("UTahData.org", 95, 22);
      pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
      pdf.line(10, 35, 200, 35);
    };

    const addFooter = (pageNumber) => {
      pdf.setFontSize(9);
      const footerY = pageHeight - 10;
      pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
      pdf.text(
        `Downloaded on: ${new Date().toLocaleDateString()}`,
        10,
        footerY,
      );
      pdf.text(
        `Page ${pageNumber}`,
        pageWidth - 10,
        footerY,
        null,
        null,
        "right",
      );
    };

    // First page
    addHeader();
    pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
    heightLeft -= pageHeight - 32;

    // Add more pages if needed
    while (heightLeft > 0) {
      pdf.addPage();
      pageNumber++;
      addHeader();
      position = heightLeft - imgHeight;
      pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;
      addFooter(pageNumber);
    }

    // Final footer
    // ===== FINAL FOOTER SAFE AREA =====

    const footerTopY = pageHeight - 40; // start footer 40mm from bottom
    const footerContentY = pageHeight - 28; // logos row
    const footerBottomTextY = pageHeight - 10; // bottom text row

    // Top Divider Line
    pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

    // UtahData logo
    const utahWidth = 18;
    const utahHeight = 18;
    pdf.addImage(
      utahData,
      "PNG",
      20,
      footerContentY - 8,
      utahWidth,
      utahHeight,
    );

    // Built by
    pdf.setFontSize(10);
    pdf.text("Built by", 45, footerContentY);

    // BODEX logo
    const bodexWidth = 36;
    const bodexHeight = bodexWidth * 0.3;
    pdf.addImage(bodex, "PNG", 60, footerContentY - 6, bodexWidth, bodexHeight);

    // Using text
    pdf.text("using", 102, footerContentY);

    // KPI logo
    pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

    // Version
    pdf.text(APP_VERSION, 140, footerContentY);

    // Bottom Info Line
    pdf.setFontSize(8);
    pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
    pdf.text(
      `Downloaded on: ${new Date().toLocaleDateString()}`,
      pageWidth - 10,
      footerBottomTextY,
      null,
      null,
      "right",
    );

    pdf.save("employment_by_race_average.pdf");
  });
};

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Employment Data by Race & Year
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Total Employment</p>
            <p className="text-xl font-bold text-blue-800">
              {grandTotal.toLocaleString()}
            </p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Highest Avg Race</p>
            <p className="text-xl font-bold text-green-800">
              {topAverageRace.race}
            </p>
          </div>

          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Overall Average</p>
            <p className="text-xl font-bold text-yellow-800">
              {Math.round(overallAverage).toLocaleString()}
            </p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Table */}
          <div className="overflow-x-auto shadow-md rounded-lg">
            <table className="w-full text-center border-collapse">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Race</th>
                  {years.map((year) => (
                    <th key={year} className="p-2">
                      {year}
                    </th>
                  ))}
                  <th className="p-2">Average</th>
                </tr>
              </thead>
              <tbody>
                {processedData.map((row) => (
                  <tr
                    key={row.race}
                    className={`${
                      row.race === topAverageRace.race
                        ? "bg-green-100 font-bold"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    <td className="p-2 font-medium">{row.race}</td>
                    {row.values.map((val, idx) => (
                      <td key={idx} className="p-2">
                        {val.toLocaleString()}
                      </td>
                    ))}
                    <td className="p-2">
                      {Math.round(row.average).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Chart */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <div className="min-w-[400px] w-full h-96">
              <Bar data={chartData} options={chartOptions} />
            </div>
          </div>
        </div>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdfIcon} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
